import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { agentsApi } from '../services/agentsApi';
import { toast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

interface CreateAgentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const CreateAgentModal: React.FC<CreateAgentModalProps> = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();

  // Form states
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!nombre.trim()) {
      toast.error('El nombre del agente es obligatorio');
      return;
    }

    setCreating(true);
    try {
      console.log('[CreateAgentModal] 🚀 Creando agente en tabla agents:', { nombre, descripcion });

      const newAgent = await agentsApi.createAgent(nombre, descripcion);

      console.log('[CreateAgentModal] ✅ Agente creado:', {
        id: newAgent.id,
        nombre: newAgent.nombre,
      });

      toast.success('Agente creado. Ahora conecta un workflow y un canal desde las pestañas.');

      onSuccess?.();
      onClose();

      console.log('[CreateAgentModal] 🎯 Navegando directamente al panel del agente:', `/agents/${newAgent.id}`);
      navigate(`/agents/${newAgent.id}`);
    } catch (error) {
      console.error('[CreateAgentModal] ❌ Error creando agente:', error);
      toast.error('Error al crear el agente');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNombre('');
    setDescripcion('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-background rounded-[20px] shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-grey-200">
          <h2 className="text-foreground text-lg font-bold font-montserrat">
            Crear Nuevo Agente
          </h2>
          <button onClick={handleClose} className="text-grey-400 hover:text-foreground transition-colors">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm mb-4">
              Crea un agente básico. Podrás conectar workflows y canales desde las pestañas dentro del agente.
            </p>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Nombre del Agente *
              </label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Agente de Ventas"
                className="w-full px-4 py-2 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Descripción (opcional)
              </label>
              <textarea
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Describe el propósito de este agente..."
                rows={3}
                className="w-full px-4 py-2 border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleClose}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreate}
                className="flex-1 bg-primary hover:bg-primary/90 text-black"
                disabled={!nombre.trim() || creating}
              >
                {creating ? 'Creando...' : 'Continuar'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateAgentModal;
